import SelectField, { SelectOption } from 'components/admin/form/selectField';
import { CSSObjectWithLabel } from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { orgSelector } from 'src/redux/selectors';
import { useUpsertUserProgram } from 'src/hooks/useProgram';
import { useOrgPrograms } from 'src/hooks/useOrgPrograms';
import { useAuth } from 'src/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getHotelStatic } from 'api/hotelApi';
import { hotelAction, orgAction } from 'src/redux/actions';
import { GENERAL_FUND } from 'src/utils/constants';
import { formatOrgName, getOrgConfiguration } from 'src/utils/organization.util';
import { isCostSavingOrg } from 'src/utils/pricing.util';
import { HiOutlineHeart } from 'react-icons/hi';
import { Popover } from 'flowbite-react';
import { useLocation, useSearchParams } from 'react-router';
type ProgramDropDownType = {
   label: string;
   value: string;
   programSlug?: string;
};
type ProgramDropDownProps = {
   className?: string;
   containerClassName?: string;
   inputClassName?: string;
   wrapperClassName?: string;
   hideTooltip?: boolean;
   hideHeart?: boolean;
   dropdownType?: string;
};
const ProgramDropDown = (props: ProgramDropDownProps) => {
   const { className, containerClassName, inputClassName, wrapperClassName, hideTooltip, hideHeart, dropdownType } = props;
   const [t] = useTranslation();
   const dispatch = useDispatch();
   const { organization } = useSelector(orgSelector);
   const defaultConfiguration: any = t('defaultConfiguration', {
      returnObjects: true,
      orgName: formatOrgName(organization, { form: 'full' }),
   });
   // NPT-1731 — the default helper text claims "proceeds" and "the general fund"
   // (a FUNDRAISE-only concept); a COST_SAVING org never distributes proceeds
   // through this picker, so it gets a savings-safe variant instead. An org's own
   // configuration.helperText (set by an admin) still wins over either default.
   if (isCostSavingOrg(organization)) {
      defaultConfiguration.helperText = t('defaultConfiguration.helperTextCostSaving');
   }
   const { options: programs } = useOrgPrograms();
   const { user } = useAuth();
   const [currProgramId, setCurrProgramId] = useState<string | null>(null);
   const [open, setOpen] = useState(false);
   const [isHovered, setIsHovered] = useState(false);
   const location = useLocation();
   const [searchParams, setSearchParams] = useSearchParams();
   const urlProgramSlug = searchParams.get('programSlug');

   const { data } = useQuery({
      queryKey: ['getHotelStatic'],
      queryFn: getHotelStatic,
      retry: false,
      staleTime: Infinity,
   });

   const { mutate: upsertUserProgram } = useUpsertUserProgram({});

   useEffect(() => {
      if (data?.data) {
         dispatch(hotelAction.setHotelStatic(data?.data));
      }
   }, [data?.data]);

   useEffect(() => {
      if (urlProgramSlug && programs.length > 0) {
         const isSlugValid = programs.some((p: any) => p.programSlug === urlProgramSlug);
         
         if (!isSlugValid) {
            setSearchParams(
               (prevParams) => {
                  const nextParams = new URLSearchParams(prevParams);
                  nextParams.delete('programSlug');
                  return nextParams;
               },
               { replace: true, state: location.state }
            );
         }
      }
   }, [urlProgramSlug, programs, setSearchParams, location.state]);

   const selectedProgramId = useMemo(() => {
      // Only ever resolve to a program the user can actually see. A previous
      // selection can outlive its visibility — it is persisted in redux and saved
      // server-side per user — so an org that has since hidden the General Fund
      // must not keep booking against it.
      const isVisible = (programId?: string) => !!programId && programs.some(p => p.value === programId);

      if (urlProgramSlug) {
         const matched = programs.find((p: any) => p.programSlug === urlProgramSlug);
         if (matched) return matched.value;
      }
      // First check if user has a saved program preference
      const userPreferredProgram = programs.find(p =>
         p.userPrograms?.some(uP => uP.userId === (user?.id as string) && uP.organizationId === organization?.id),
      )?.value;
      if (userPreferredProgram) return userPreferredProgram;

      // If guest or no preference, use current selection
      if (currProgramId && isVisible(currProgramId)) return currProgramId;

      // Default to "General Fund" program if it is still one of the options
      const generalFundProgram = programs.find(p => p.label === GENERAL_FUND)?.value;
      if (generalFundProgram) return generalFundProgram;

      // Fall back to VITE_DEFAULT_PROGRAM_ID (itself the General Fund) or first program
      const envDefault = `${import.meta.env.VITE_DEFAULT_PROGRAM_ID}`;
      return isVisible(envDefault) ? envDefault : programs[0]?.value;
   }, [programs, urlProgramSlug, user?.id, currProgramId, organization?.id]);

   const onSelectProgram = (programId: string) => {
      if (user) {
         upsertUserProgram({ programId, organizationId: organization?.id });
      } else {
         setCurrProgramId(programId);
      }
      const program: ProgramDropDownType = programs.find((p: { value: string }) => p?.value === programId) as ProgramDropDownType;
      dispatch(orgAction.setProgram(program));
      dispatch(orgAction.setProgramId(programId as any));
      setSearchParams(
         prevParams => {
            const nextParams = new URLSearchParams(prevParams);

            if (program.programSlug) {
               nextParams.set('programSlug', program.programSlug);
            } else {
               nextParams.delete('programSlug');
            }

            return nextParams;
         },
         { replace: true, state: location.state },
      );
   };

   const configuration = useMemo(() => getOrgConfiguration(organization, defaultConfiguration), [organization]);

   useEffect(() => {
      const program: ProgramDropDownType = programs.find((p: { value: string }) => p?.value === selectedProgramId) as ProgramDropDownType;
      dispatch(orgAction.setProgram(program));
      dispatch(orgAction.setProgramId(selectedProgramId as any));
   }, [selectedProgramId]);

   return (
      programs &&
      programs.length > 1 && (
         <div className={`program-wrapper flex-1 w-full xl:w-[195px] 2xl:w-[220px] ${className}`}>
            <Popover
               theme={{ base: 'border-0', arrow: { base: 'bg-white border-none' } }}
               content={
                  !hideTooltip && (
                     <div className="bg-white p-3.75 shadow-lg rounded-md border border-gray-100">
                        <div className="text-sm leading-5 font-normal text-black line-clamp-2 w-fit max-w-60">{configuration?.helperText}</div>
                     </div>
                  )
               }
               trigger="hover"
               placement="top"
               open={!hideTooltip && isHovered && !open}
            >
               <div
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
               >
                  <div className={`flex justify-end items-center h-14 gap-2.5 w-full ${wrapperClassName ?? ''}`}>
                     <SelectField
                        startIcon={hideHeart ? undefined : HiOutlineHeart}
                        theme={{
                           container: `w-full min-h-8 ${open ? 'border-2 border-orgPrimary' : ''} rounded-10 ${containerClassName}`,
                           border: `bg-white !rounded-10 ${open ? 'border-0' : 'border-borderStrong'}`,
                           input: `h-13.5 min-h-8 w-full rounded-10 ${inputClassName}`,
                        }}
                        menuPortalTarget={null}
                        menuPosition="absolute"
                        value={selectedProgramId}
                        options={programs}
                        onChange={(value: SelectOption | SelectOption[]) => {
                           const newProgramId = (value as SelectOption).value;

                           onSelectProgram(newProgramId);
                        }}
                        onFocus={focus => setOpen(focus)}
                        styles={{
                           menu: (base: CSSObjectWithLabel) => ({
                              ...base,
                              border: 'none',
                              borderRadius: '6px',
                              boxShadow: '1px 1px 20px 0px #0000001F',
                              zIndex: 9999,
                              left: '0',
                              marginTop: '4px',
                           }),
                           control: (base: CSSObjectWithLabel) => ({
                              ...base,
                              minHeight: '44px',
                              border: 'none',
                              boxShadow: 'none',
                              borderRadius: '10px',
                              cursor: 'pointer',
                           }),
                           singleValue: (base: CSSObjectWithLabel) => ({
                              ...base,
                              flexWrap: 'nowrap',
                              fontSize: '18px',
                              lineHeight: '18px',
                              maxWidth: '89%',
                           }),
                           ...(configuration.color && {
                              option: (base: CSSObjectWithLabel, { isFocused }) => {
                                 return {
                                    ...base,
                                    backgroundColor: isFocused && configuration.color?.lightPrimary,
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    lineHeight: '18px',
                                    ':hover': {
                                       backgroundColor: configuration.color?.lightPrimary,
                                    },
                                 };
                              },
                           }),
                           indicatorsContainer(base: CSSObjectWithLabel) {
                              return {
                                 ...base,
                                 svg: {
                                    width: '28px',
                                 },
                              };
                           },
                        }}
                        dropdownType={dropdownType}
                     />
                  </div>
               </div>
            </Popover>
         </div>
      )
   );
};

export default ProgramDropDown;
